// Single-value stores backed by a 1-element array so the reference
// stays stable for any module that captured it at import time.
const singleStore = (fallback = null) => {
  const slot = [];
  return {
    set: (data) => {
      slot.length = 0;
      slot.push(data);
    },
    get: () => (slot.length > 0 ? slot[0] : fallback),
    clear: () => {
      slot.length = 0;
    },
    raw: slot,
  };
};

// Region / Country / Level navigation arrays are exported directly
// because some consumers read them as arrays.
export const SysRegionNavigation = [];
export const SysCountryNavigation = [];
export const SysLevelNavigation = [];

const replaceFirst = (target, data) => {
  target.length = 0;
  target.push(data);
};

export const pushRegion = (data) => replaceFirst(SysRegionNavigation, data);
export const pushCountry = (data) => replaceFirst(SysCountryNavigation, data);
export const pushLevel = (data) => replaceFirst(SysLevelNavigation, data);

export const getRegion = () => SysRegionNavigation[0];
export const getRegionName = () =>
  SysRegionNavigation.length > 0 ? SysRegionNavigation[0].name : "Regions";

export const getCountry = () =>
  SysCountryNavigation.length > 0 ? SysCountryNavigation[0] : null;
export const getCountryName = () =>
  SysCountryNavigation.length > 0
    ? SysCountryNavigation[0].country.name
    : "Countries";

export const getLevelName = () => SysLevelNavigation[0].level.name;
export const getLevelRoute = () => SysLevelNavigation[0].level.route;

export const displayCountry = () =>
  SysRegionNavigation.length > 0 && SysRegionNavigation[0] !== null;
export const displayRegion = () =>
  SysCountryNavigation.length > 0 && SysRegionNavigation.length > 0;
export const displayLevel = () => SysLevelNavigation.length > 0;

const currentRegion = singleStore();
export const pushCurrentRegion = currentRegion.set;
export const getCurrentRegion = () => currentRegion.raw;

const singleLevelDashboard = singleStore("Level");
export const pushSingleLevelDashboard = singleLevelDashboard.set;
export const getSingleLevelDashboard = singleLevelDashboard.get;

const cityDashboard = singleStore("city");
export const pushCityDashboard = cityDashboard.set;
export const getCityDashboard = cityDashboard.get;

const singleCountryDashboard = singleStore("Country");
export const pushSingleCountryDashboard = singleCountryDashboard.set;
export const getSingleCountryDashboard = singleCountryDashboard.get;

const editLevel = singleStore();
export const pushEditLevel = editLevel.set;
export const getEditLevel = editLevel.get;

const addLevel = singleStore();
export const pushAddLevel = addLevel.set;
export const getAddLevel = addLevel.get;

// Generic single-record stores for every content type.
// Each store yields a `pushOne<Name>` setter and `getOne<Name>` reader.
const currency = singleStore();
export const pushOneCurrency = currency.set;
export const getOneCurrency = currency.get;

const privacy = singleStore();
export const pushOnePrivacy = privacy.set;
export const getOnePrivacy = privacy.get;

const terms = singleStore();
export const pushOneTerms = terms.set;
export const getOneTerms = terms.get;

const regionalBlocks = singleStore();
export const pushOneRegionalBlocks = regionalBlocks.set;
export const getOneRegionalBlocks = regionalBlocks.get;

const company = singleStore();
export const pushOneCompany = company.set;
export const getOneCompany = company.get;

const banner = singleStore();
export const pushOneBanner = banner.set;
export const getOneBanner = banner.get;

const email = singleStore();
export const pushOneEmail = email.set;
export const getOneEmail = email.get;

const museum = singleStore();
export const pushOneMuseum = museum.set;
export const getOneMuseum = museum.get;

const partner = singleStore();
export const pushOnePartner = partner.set;
export const getOnePartner = partner.get;

const page = singleStore();
export const pushOnePage = page.set;
export const getOnePage = page.get;

const team = singleStore();
export const pushOneTeam = team.set;
export const getOneTeam = team.get;

const advertisement = singleStore();
export const pushOneAdvertisement = advertisement.set;
export const getOneAdvertisement = advertisement.get;

const tipsAndAdvice = singleStore();
export const pushOneTipsAndAdvice = tipsAndAdvice.set;
export const getOneTipsAndAdvice = tipsAndAdvice.get;

const theater = singleStore();
export const pushOneTheater = theater.set;
export const getOneTheater = theater.get;

const dance = singleStore();
export const pushOneDance = dance.set;
export const getOneDance = dance.get;

const movie = singleStore();
export const pushOneMovie = movie.set;
export const getOneMovie = movie.get;

const art = singleStore();
export const pushOneArt = art.set;
export const getOneArt = art.get;

const gettingAround = singleStore();
export const pushOneGettingAround = gettingAround.set;
export const getOneGettingAround = gettingAround.get;

const ministry = singleStore();
export const pushOneMinistry = ministry.set;
export const getOneMinistry = ministry.get;

const companies = singleStore();
export const pushOneCompanies = companies.set;
export const getOneCompanies = companies.get;

const embassy = singleStore();
export const pushOneEmbassy = embassy.set;
export const getOneEmbassy = embassy.get;

const neighborhood = singleStore();
export const pushOneNeighborhood = neighborhood.set;
export const getOneNeighborhood = neighborhood.get;

const book = singleStore();
export const pushOneBook = book.set;
export const getOneBook = book.get;

const food = singleStore();
export const pushOneFood = food.set;
export const getOneFood = food.get;

const shopping = singleStore();
export const pushOneShopping = shopping.set;
export const getOneShopping = shopping.get;

const classes = singleStore();
export const pushOneClasses = classes.set;
export const getOneClasses = classes.get;

const hotel = singleStore();
export const pushOneHotel = hotel.set;
export const getOneHotel = hotel.get;

const cityGuide = singleStore();
export const pushOneCityGuide = cityGuide.set;
export const getOneCityGuide = cityGuide.get;

const nightlife = singleStore();
export const pushOneNightlife = nightlife.set;
export const getOneNightlife = nightlife.get;

const party = singleStore();
export const pushOneParty = party.set;
export const getOneParty = party.get;

const thingsToDo = singleStore();
export const pushOneThingsToDo = thingsToDo.set;
export const getOneThingsToDo = thingsToDo.get;

const admin = singleStore();
export const pushOneAdmin = admin.set;
export const getOneAdmin = admin.get;

const user = singleStore();
export const pushOneUser = user.set;
export const getOneUser = user.get;

const agent = singleStore();
export const pushOneAgent = agent.set;
export const getOneAgent = agent.get;

const product = singleStore();
export const pushOneProduct = product.set;
export const getOneProduct = product.get;

const tourPackage = singleStore();
export const pushOneTourPackage = tourPackage.set;
export const getOneTourPackage = tourPackage.get;

const blog = singleStore();
export const pushOneBlog = blog.set;
export const getOneBlog = blog.get;

const footer = singleStore();
export const pushOneFooter = footer.set;
export const getOneFooter = footer.get;

const about = singleStore();
export const pushOneAbout = about.set;
export const getOneAbout = about.get;

const service = singleStore();
export const pushOneService = service.set;
export const getOneService = service.get;

const restaurant = singleStore();
export const pushOneRestaurant = restaurant.set;
export const getOneRestaurant = restaurant.get;

const safari = singleStore();
export const pushOneSafari = safari.set;
export const getOneSafari = safari.get;

const news = singleStore();
export const pushOneNews = news.set;
export const getOneNews = news.get;

const music = singleStore();
export const pushOneMusic = music.set;
export const getOneMusic = music.get;

const cinema = singleStore();
export const pushOneCinema = cinema.set;
export const getOneCinema = cinema.get;

const bestOfAfrica = singleStore();
export const pushOneBestOfAfrica = bestOfAfrica.set;
export const getOneBestOfAfrica = bestOfAfrica.get;

const event = singleStore();
export const pushOneEvent = event.set;
export const getOneEvent = event.get;

const attraction = singleStore();
export const pushOneAttraction = attraction.set;
export const getOneAttraction = attraction.get;

const tradeOrganization = singleStore();
export const pushOneTradeOrganization = tradeOrganization.set;
export const getOneTradeOrganization = tradeOrganization.get;

const climate = singleStore();
export const pushOneClimate = climate.set;
export const getOneClimate = climate.get;

const ethnicGroup = singleStore();
export const pushOneEthnicGroup = ethnicGroup.set;
export const getOneEthnicGroup = ethnicGroup.get;

const language = singleStore();
export const pushOneLanguage = language.set;
export const getOneLanguage = language.get;

const religion = singleStore();
export const pushOneReligion = religion.set;
export const getOneReligion = religion.get;

const plugImage = singleStore();
export const pushOnePlugImage = plugImage.set;
export const getOnePlugImage = plugImage.get;

const aiChatInfo = singleStore();
export const pushOneAiChatInfo = aiChatInfo.set;
export const getOneAiChatInfo = aiChatInfo.get;

export const clearRoutingHistory = () => {
  currentRegion.clear();
  singleLevelDashboard.clear();
  singleCountryDashboard.clear();
  SysLevelNavigation.length = 0;
  SysRegionNavigation.length = 0;
  SysCountryNavigation.length = 0;
};
